// src/utils/loadBlockchain.js

import { ethers } from 'ethers';
import CashStakingABI from '../abi/CashStaking.json';
import CashMiningABI from '../abi/CashMining.json';
import CashTokenABI from '../abi/CashisDead.json';
import CashSwapABI from '../abi/CashSwap.json';
import CashNFTABI from '../abi/CashNFT.json';
import BoosterNFTABI from '../abi/CashBoosterNFT.json';
import LPStakingABI from '../abi/LPCashStake.json';
import {
  CASHSTAKING_ADDRESS,
  CASHMINING_ADDRESS,
  CASHISDEAD_ADDRESS,
  CASHSWAP_ADDRESS,
  CASHNFT_ADDRESS,
  BOOSTERNFT_ADDRESS,
  LPCASHSTAKE_ADDRESS,
} from '../contracts/constants';

/**
 * Utility function to enforce checksum on addresses.
 * @param {string} address - The contract address.
 * @returns {string} - Checksummed address.
 */
const getChecksummedAddress = (address) => {
  try {
    return ethers.utils.getAddress(address);
  } catch (error) {
    console.error(`Invalid address checksum for address: ${address}`, error);
    return null;
  }
};

/**
 * Loads the Staking Contract connected to a signer.
 * @param {ethers.providers.Web3Provider} provider
 * @returns {ethers.Contract|null}
 */
export const loadStakingContract = (provider) => {
  if (!provider) {
    return null;
  }
  try {
    const signer = provider.getSigner();
    const checksummedAddress = getChecksummedAddress(CASHSTAKING_ADDRESS);
    if (!checksummedAddress) return null;
    return new ethers.Contract(checksummedAddress, CashStakingABI, signer);
  } catch (error) {
    console.error("Error loading Staking Contract:", error);
    return null;
  }
};

/**
 * Loads the Mining Contract connected to a signer.
 * @param {ethers.providers.Web3Provider} provider
 * @returns {ethers.Contract|null}
 */
export const loadMiningContract = (provider) => {
  if (!provider) {
    return null;
  }
  try {
    const signer = provider.getSigner();
    const checksummedAddress = getChecksummedAddress(CASHMINING_ADDRESS);
    if (!checksummedAddress) return null;
    return new ethers.Contract(checksummedAddress, CashMiningABI, signer);
  } catch (error) {
    console.error("Error loading Mining Contract:", error);
    return null;
  }
};

/**
 * Loads the Token Contract connected to a provider.
 * @param {ethers.providers.Provider} provider
 * @returns {ethers.Contract|null}
 */
export const loadTokenContract = (provider) => {
  if (!provider) {
    return null;
  }
  try {
    const checksummedAddress = getChecksummedAddress(CASHISDEAD_ADDRESS);
    if (!checksummedAddress) return null;
    return new ethers.Contract(checksummedAddress, CashTokenABI, provider);
  } catch (error) {
    console.error("Error loading Token Contract:", error);
    return null;
  }
};

/**
 * Loads the CashSwap Contract connected to a signer.
 * @param {ethers.providers.Web3Provider} provider
 * @returns {ethers.Contract|null}
 */
export const loadCashSwapContract = (provider) => {
  if (!provider) {
    return null;
  }
  try {
    const signer = provider.getSigner();
    const checksummedAddress = getChecksummedAddress(CASHSWAP_ADDRESS);
    if (!checksummedAddress) return null;
    return new ethers.Contract(checksummedAddress, CashSwapABI, signer);
  } catch (error) {
    console.error("Error loading CashSwap Contract:", error);
    return null;
  }
};

/**
 * Loads the CashNFT Contract connected to a signer.
 * @param {ethers.providers.Web3Provider} provider
 * @returns {ethers.Contract|null}
 */
export const loadCashNFTContract = async (provider) => {
  if (!provider) {
    console.error('Provider not available');
    return null;
  }

  try {
    const checksummedAddress = getChecksummedAddress(CASHNFT_ADDRESS);
    if (!checksummedAddress) return null;

    const code = await provider.getCode(checksummedAddress);
    if (code === '0x') {
      console.error('NFT Contract not deployed at address:', checksummedAddress);
      return null;
    }

    const signer = provider.getSigner();
    const contract = new ethers.Contract(checksummedAddress, CashNFTABI, signer);

    try {
      const miningContractAddress = await contract.cashMiningContract();
      const stakingContractAddress = await contract.cashStakingContract();
      const swapContractAddress = await contract.cashSwapContract();

      const [miningCode, stakingCode, swapCode] = await Promise.all([
        provider.getCode(miningContractAddress),
        provider.getCode(stakingContractAddress),
        provider.getCode(swapContractAddress)
      ]);

      if (miningCode === '0x') {
        throw new Error(`Mining contract not deployed at: ${miningContractAddress}`);
      }
      if (stakingCode === '0x') {
        throw new Error(`Staking contract not deployed at: ${stakingContractAddress}`);
      }
      if (swapCode === '0x') {
        throw new Error(`Swap contract not deployed at: ${swapContractAddress}`);
      }

      const isERC721 = await contract.supportsInterface('0x80ac58cd').catch(() => false);
      if (!isERC721) {
        throw new Error('Contract does not support ERC721 interface');
      }

      return contract;
    } catch (error) {
      console.error('Failed to verify contract dependencies:', error);
      return null;
    }
  } catch (error) {
    console.error("Error loading CashNFT Contract:", error);
    return null;
  }
};

/**
 * Loads the BoosterNFT Contract connected to a signer.
 * @param {ethers.providers.Web3Provider} provider
 * @returns {ethers.Contract|null}
 */
export const loadBoosterNFTContract = (provider) => {
  if (!provider) {
    return null;
  }
  try {
    const signer = provider.getSigner();
    const checksummedAddress = getChecksummedAddress(BOOSTERNFT_ADDRESS);
    if (!checksummedAddress) return null;
    return new ethers.Contract(checksummedAddress, BoosterNFTABI, signer);
  } catch (error) {
    console.error("Error loading BoosterNFT Contract:", error);
    return null;
  }
};

/**
 * Loads the LP Staking Contract connected to a signer.
 * @param {ethers.providers.Web3Provider} provider
 * @returns {ethers.Contract|null}
 */
export const loadLPStakingContract = (provider) => {
  if (!provider) {
    return null;
  }
  try {
    const signer = provider.getSigner();
    const checksummedAddress = getChecksummedAddress(LPCASHSTAKE_ADDRESS);
    if (!checksummedAddress) return null;
    return new ethers.Contract(checksummedAddress, LPStakingABI, signer);
  } catch (error) {
    console.error("Error loading LP Staking Contract:", error);
    return null;
  }
};

/**
 * Loads a generic ERC20 token contract connected to a signer.
 * @param {ethers.providers.Web3Provider} provider
 * @param {string} tokenAddress - The address of the ERC20 token
 * @returns {ethers.Contract|null}
 */
export const loadERC20TokenContract = (provider, tokenAddress) => {
  if (!provider || !tokenAddress) {
    return null;
  }
  
  // Standard ERC20 ABI with common functions
  const ERC20_ABI = [
    "function name() view returns (string)",
    "function symbol() view returns (string)",
    "function decimals() view returns (uint8)",
    "function totalSupply() view returns (uint256)",
    "function balanceOf(address) view returns (uint256)",
    "function transfer(address to, uint amount) returns (bool)",
    "function allowance(address owner, address spender) view returns (uint256)",
    "function approve(address spender, uint256 amount) returns (bool)",
    "function transferFrom(address from, address to, uint256 amount) returns (bool)",
    "event Transfer(address indexed from, address indexed to, uint amount)",
    "event Approval(address indexed owner, address indexed spender, uint256 value)"
  ];
  
  try {
    const signer = provider.getSigner();
    const checksummedAddress = getChecksummedAddress(tokenAddress);
    if (!checksummedAddress) return null;
    return new ethers.Contract(checksummedAddress, ERC20_ABI, signer);
  } catch (error) {
    console.error(`Error loading ERC20 Token Contract at ${tokenAddress}:`, error);
    return null;
  }
};